import { ITrainMeta } from '@/models/Train/interfaces/ITrainReserveSerialized';
import { ITransportationSearchTrain } from '@/models/Transportation/interfaces/payloads/ITransportationSearchTrainPayload';
import { ITrainFareInfo } from '@/models/Transportation/interfaces/payloads/ITransportationSearchTrainSeatMapCarriagesPayload';
import StoreService, { BrowserStorage } from '@/services/StoreService/StoreService';
import { TTrainClassifyTypeName } from '@/stores/TrainStore/TrainTicketsStore';
import { action, observable } from 'mobx';
import { mergeDeepRight } from 'ramda';

export interface ITrainValidateItem {
  seats: {
    /** 호차번호 */
    carriageNo: string;
    /** 좌석번호 */
    seatNo: string;
    /** 좌석 코드 */
    seatDescription: string;
  }[];
  /** 도착 시각 */
  arrivalTime: string;
  /** 도착 일자 */
  arrivalDate: string;
  /** 도착역 코드 */
  arrivalStationCode: string;
  /**  */
  arrivalStationName: string;
  /** 출발 시각 */
  departureTime: string;
  /** 출발 일자 */
  departureDate: string;
  /** 출발역 코드 */
  departureStationCode: string;
  /**  */
  departureStationName: string;
  /** 열차번호 */
  trainNo: string;
  /** 상품번호 */
  goodsNo: string;
  /** 상품 품목 ID */
  goodsItemId: string;
  /** 상품구성품목 ID */
  goodsGroupItemId: string;
  /** 상품구성일련번호 */
  goodsGroupSequenceNo: string;
  /** 100 KTX */
  trainGroupCode: string;
  /** 객실 등급 코드(1 일반실) */
  carriageClassCode?: string;
  /** 역무열차종별 코드(00 KTX) */
  stationTrainClassifyCode: string;
  /** 운행일 */
  runningDate: string;
  trainClassifyImage: {
    h16_url: string;
    h20_url: string;
    width: number;
    height: number;
  };
  departureTimeDisplay: string;
  arrivalTimeDisplay: string;
  carriageName: string;
  point: number;
  totalPrice: number;

  /* KTX-산천-131 */
  /* KTX-산천 */
  stationTrainClassifyName: string;
  /* 131 */
  trainNoDisplay: string;
  discount: {
    /* 묶음할인금액 */
    amount: number;
    /* 묶음할인율 */
    ratio: number;
  };
  /* SRP 선택된 가격 정보 */
  fareInfo: { [goodsItemId: string]: ITrainFareInfo };
  /* SRP 저장 시간 10분간 유효 */
  savedTicketsTime: number;
  /* SRP 선택한 티켓 정보 */
  selectedTicket: ITransportationSearchTrain | null;
  /* SRP 시간 검색 조건 */
  selectedFilteringDepartureTime: string;
  /* SRP 시간 티켓 offsetTop */
  selectedTicketOffsetTop: number;
  /* SRP 열차 종류 필터 타입 */
  selectedFilteringClassifyType: TTrainClassifyTypeName;
}
export interface ITrainExpandedMeta extends Omit<ITrainMeta, 'roundtripLeg'> {
  tripType?: 'ONEWAY' | 'ROUND';
  roundtripLeg?: 'outbound' | 'return';
}
interface ITrainTrains {
  departTrain: ITrainValidateItem;
  returnTrain: ITrainValidateItem;
}
export interface ITrainBaseStorage extends ITrainTrains {
  adults: number;
  child: number;
}

export type TDirection = keyof ITrainTrains;
export type TTripType = 'one' | 'round';

const STORAGE_KEY = 'trainStorage';

/** encodedQuery 디코드 결과 중 검색조건에 해당하는것만  */
export interface IEncodedTrainSegment {
  departureDate: string;
  departureStationCode: string;
  departureStationName: string;
  arrivalStationCode: string;
  arrivalStationName: string;
}

export interface IEncodedTrainQuery {
  adults: number;
  child: number;
  departTrain?: Partial<IEncodedTrainSegment>;
  returnTrain?: Partial<IEncodedTrainSegment>;
}

export interface INormalizedEncodedTrainQuery {
  tripType: TTripType;
  directionType: TDirection;
  storageData: DeepPartial<ITrainBaseStorage>;
}

const isCompleteEncodedSegment = (segment: unknown): segment is IEncodedTrainSegment => {
  if (!segment || typeof segment !== 'object') {
    return false;
  }
  const { departureDate, departureStationCode, departureStationName, arrivalStationCode, arrivalStationName } =
    segment as Record<string, unknown>;
  return (
    typeof departureDate === 'string' &&
    typeof departureStationCode === 'string' &&
    typeof departureStationName === 'string' &&
    typeof arrivalStationCode === 'string' &&
    typeof arrivalStationName === 'string'
  );
};

/**
 * - departTrain 있음 + returnTrain 있음 → 왕복
 * - departTrain 있음 + returnTrain 없음 or 데이터 내에 누락된게 있음 → 편도
 * - departTrain 없음 or 데이터 누락 → returnTrain 존재 여부와 무관하게 error 페이지 (as-is 방식으로 fallback)
 */
export const normalizeEncodedTrainQuery = (raw: Record<string, unknown> | null): INormalizedEncodedTrainQuery | null => {
  if (!raw) {
    return null;
  }

  const adults = typeof raw.adults === 'number' ? raw.adults : 1;
  const child = typeof raw.child === 'number' ? raw.child : 0;
  const hasDepart = isCompleteEncodedSegment(raw.departTrain);
  const hasReturn = isCompleteEncodedSegment(raw.returnTrain);

  if (!hasDepart) {
    return null;
  }

  if (hasReturn) {
    return {
      tripType: 'round',
      directionType: 'departTrain',
      storageData: {
        adults,
        child,
        departTrain: raw.departTrain as IEncodedTrainSegment,
        returnTrain: raw.returnTrain as IEncodedTrainSegment,
      },
    };
  }

  return {
    tripType: 'one',
    directionType: 'departTrain',
    storageData: { adults, child, departTrain: raw.departTrain as IEncodedTrainSegment },
  };
};

/** 정규화된 encodedQuery 데이터를 sessionStorage(trainStorage)에 반영한다. */
export const writeEncodedTrainQueryToStorage = (data: DeepPartial<ITrainBaseStorage>): void => {
  if (!process.browser) {
    return;
  }
  const storage = StoreService.getInstance('session');
  const existing = storage.get(STORAGE_KEY, {}) as ITrainBaseStorage;
  storage.set(STORAGE_KEY, mergeDeepRight(existing, data));
};

export default abstract class TrainSessionStore extends StoreService {
  @observable storage: BrowserStorage;

  @observable isSnackbarVisible: boolean = false;

  private key: string = STORAGE_KEY;

  constructor() {
    super();
    this.storage = StoreService.getInstance('session');
  }

  @action.bound
  getStorage(): ITrainBaseStorage {
    if (!process.browser) {
      return {} as ITrainBaseStorage;
    }
    return this.storage.get(this.key, {}) as ITrainBaseStorage;
  }

  @action.bound
  modify(data: DeepPartial<ITrainBaseStorage>): void {
    const storageObj = mergeDeepRight(this.getStorage(), data);
    this.storage.set(this.key, storageObj);
  }

  @action.bound
  cleanStorage(): void {
    this.storage.remove(this.key);
  }

  @action.bound
  getProps<U = DeepPartial<ITrainBaseStorage>>(obj: Record<string, any> = {}, keys: string[] = []): U {
    return keys.reduce((acc, cur) => {
      if (obj[cur]) {
        return { ...acc, [cur]: obj[cur] };
      }
      return acc;
    }, {} as U);
  }

  @action.bound
  getTrainMeta(directionType: TDirection, tripType: TTripType): ITrainExpandedMeta {
    const storage = this.getStorage();
    const trainMeta = storage[directionType];

    return {
      departureStation: trainMeta.departureStationName,
      departureStationCode: trainMeta.departureStationCode,
      arrivalStation: trainMeta.arrivalStationName,
      arrivalStationCode: trainMeta.arrivalStationCode,
      numAdults: storage.adults,
      numChildren: storage.child,
      trainClassifyNo: trainMeta.stationTrainClassifyCode || '',
      trainNo: trainMeta.trainNo || '',
      roundtripLeg: directionType === 'returnTrain' ? 'return' : 'outbound',
      tripType: tripType === 'one' ? 'ONEWAY' : 'ROUND',
      trainSeatSet:
        trainMeta.seats?.map((seat) => ({
          trainCarriageName: trainMeta.carriageName,
          trainSeatName: seat.seatDescription,
        })) || [],
      departDate: trainMeta.departureDate,
    };
  }

  @action.bound
  openSnackbar(): void {
    this.isSnackbarVisible = true;
  }

  @action.bound
  closeSnackbar(): void {
    this.isSnackbarVisible = false;
  }
}
